/**
 * Words several storefront pages say that belong to none of them, and the rule for this folder.
 *
 * `storefrontCopy/` is the storefront's default copy, a module per page (named for its folder
 * under apps/storefront/src/templates/pages/) plus `frame.ts`: the words a store shows where it has
 * written none, in its terms (`storefrontTerminology.ts`), name and currency. Plain strings and
 * in-store paths, never components, so the storefront's content hooks and the dashboard's theme
 * preview read the same text; they are also the defaults a store's own copy will be laid over
 * (docs/design-system/templates.md, "Tenant copy").
 *
 * Default copy is a store's own words: every heading, intro, label, link and button label, tab,
 * chip and sort label, placeholder, helper line, empty state, status label, confirmation and call
 * to action on a page. Platform words are the same on every store and never a store's to change:
 * errors and load-failure fallbacks, refusals and validation messages, write outcomes (toasts),
 * legal text and assents, compliance disclosures, the fairness readout, and the controls of the
 * opener and the battle claim. Platform words stay with the code that shows them or, when the
 * theme preview draws one too, go in an api-contract module named for their concern (the footer's
 * legal line is in `storefrontLegal.ts`), never in this folder.
 */
import type { StorefrontTerminology } from "../storefrontTerminology.js";
import type { VolatilityLevel } from "../volatility.js";

/** A run of prose: plain text, or text linking to a page of the store. */
export type CopyRun = string | { text: string; href: string };

/** The headings over the shopper's best recent opens: Home's, and the catalog's. */
export const RECENT_OPENS_TITLES = { home: "Fire pulls", catalog: "Top hits" } as const;

/** A paged list's button, and the count line announced after each page loads. */
export const STOREFRONT_PAGING_COPY = {
  loadMore: "Load more",
  showing: (shown: number) => `Showing ${shown}`,
} as const;

/** A pull tile's button, which opens the pull's details, read with the item's name. */
export function pullDetailsLabel(itemName: string): string {
  return `Show ${itemName}`;
}

/** A box tile's heart, read with the box's name. */
export function favoriteBoxLabel(boxName: string): string {
  return `Favorite ${boxName}`;
}

/** A volatility meter's words: its name, and each level's word, read in place of a number. */
export interface VolatilityWords {
  label: string;
  levels: Readonly<Record<VolatilityLevel, string>>;
}

/** The volatility meter on a box tile and the box page. */
export const VOLATILITY_WORDS: VolatilityWords = {
  label: "Volatility",
  levels: { low: "Low", medium: "Medium", high: "High", max: "Max" },
};

/** The link to the catalog, in the store's word for its boxes. */
export function browseBoxesLabel(terms: StorefrontTerminology): string {
  return `Browse ${terms.box.plural}`;
}

/** The link to Add funds beside a shortfall, in the currency's plural name. */
export function buyCurrencyLabel(currencyPlural: string): string {
  return `Buy ${currencyPlural}`;
}
